import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { centsToDollars, centsToDollarsNumber, getLocalizedPricing } from '../../lib/util';
import ConfirmModal from '../../components/modals/ConfirmModal';
import * as events from '../../lib/analytics/events';
import { SubscriptionOption } from '../../components/SubscriptionOptionDetails';
import { DateTime } from 'luxon';
import styles from '../ManageSubscription/styles/SubscriptionContainer.module.scss';
import { useLocale } from '../../contexts/LocaleContext';

interface IMonthlyUpgradeConfirmationModalProps {
  onContinue: () => void;
  subscriptionOption?: SubscriptionOption;
  previewRenewalDate: string;
  trigger: React.ReactElement;
  open?: boolean;
  upgradeNow?: boolean;
}

export default function MonthlyUpgradeConfirmationModal({
  onContinue,
  trigger,
  subscriptionOption,
  previewRenewalDate,
  open,
  upgradeNow,
}: IMonthlyUpgradeConfirmationModalProps) {
  const { currency } = useLocale();
  const { t } = useTranslation();
  const onAccept = () => {
    events.monthlyUpgrade.confirmationModalContinue({ newSku: subscriptionOption?.sku });
    onContinue();
  };
  const onDecline = () => {
    events.monthlyUpgrade.confirmationModalGoBack({ newSku: subscriptionOption?.sku });
  };

  const getBillingCadence = (subscription: SubscriptionOption): string => {
    if (subscription.renewalMonths === 1) {
      return t('every_month');
    } else if (subscription.renewalMonths % 12 === 0) {
      const years = Math.round(subscription.renewalMonths / 12);
      if (years === 1) {
        return t('annually');
      } else if (years === 2) {
        return t('biennially');
      } else {
        return t('every_years_years', { years });
      }
    } else {
      return t('every_months_months', { months: subscription.renewalMonths });
    }
  };

  if (upgradeNow) {
    return (
      <ConfirmModal
        open={open}
        trigger={trigger}
        body={
          <>
            <h2>{t('confirm_new_plan')}</h2>
            <p>
              <Trans
                i18nKey="your_membership_will_switch_now_and_you_will_be_charged_amount_then_price_cadence_starting_on_date"
                components={{
                  price: <span className={styles.emphasize} />,
                  date: <span className={styles.emphasize} />,
                }}
                values={{
                  amount: centsToDollars(
                    subscriptionOption
                      ? (subscriptionOption.planSwitchAmountChargedInCents ??
                          getLocalizedPricing(subscriptionOption.pricingInCents, currency))
                      : 0,
                  ),
                  price: centsToDollarsNumber(
                    subscriptionOption ? getLocalizedPricing(subscriptionOption.pricingInCents, currency) : 0,
                  ),
                  cadence: subscriptionOption ? getBillingCadence(subscriptionOption) : '',
                  date: DateTime.fromISO(previewRenewalDate).toFormat('M/d/yyyy'),
                }}
              />
            </p>
          </>
        }
        acceptText={t('confirm')}
        declineText={t('go_back')}
        onAccept={onAccept}
        onDecline={onDecline}
      />
    );
  }

  return (
    <ConfirmModal
      open={open}
      trigger={trigger}
      body={
        <>
          <h2>{t('confirm_new_plan')}</h2>
          <p>
            <Trans
              i18nKey="your_membership_will_renew_on_date_and_you_will_be_charged_amount_cadence"
              components={{
                price: <span className={styles.emphasize} />,
                date: <span className={styles.emphasize} />,
              }}
              values={{
                date: DateTime.fromISO(previewRenewalDate).toFormat('M/d/yyyy'),
                amount: centsToDollarsNumber(
                  subscriptionOption ? getLocalizedPricing(subscriptionOption.pricingInCents, currency) : 0,
                ),
                cadence: subscriptionOption ? getBillingCadence(subscriptionOption) : '',
              }}
            />
          </p>
        </>
      }
      acceptText={t('confirm')}
      declineText={t('go_back')}
      onAccept={onAccept}
      onDecline={onDecline}
    />
  );
}
